import { useEffect, useRef, useState } from 'preact/hooks';
import type { SliderWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { formatNumber } from '../../format/leafValue';
import './SliderRenderer.css';

interface Props {
  widget: SliderWidget;
  source: IPointBindingSource;
}

/**
 * Slider with commit-on-release. Concept §3: no live write while dragging;
 * the renderer only fires when the operator lets go AND the delta against
 * the captured baseline exceeds `deadband`. The feedback point renders both
 * as the big readout and as a small tick on the track, so the operator can
 * see the controller's actual position independent of the drag handle.
 *
 * If the operator lifts inside the deadband, the drag snaps back to the
 * feedback value — the commit path is never triggered. Otherwise the write
 * enters the shared Pending / Confirmed / Rejected lifecycle.
 */
export function SliderRenderer({ widget, source }: Props) {
  const { value, release } = pointSignal(source, widget.feedbackPoint);
  useEffect(() => release, [release]);

  const [pending, setPending] = useState<{ target: number; state: 'sending' | 'rejected' } | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);
  const dragBaselineRef = useRef<number | null>(null);

  const current = value.value;
  const stale = isDegraded(current);
  const feedbackNumber = typeof current?.value === 'number' && Number.isFinite(current.value)
    ? current.value
    : null;

  const deadband = widget.deadband ?? 0;
  const step = widget.step ?? undefined;
  const trackValue = dragValue ?? pending?.target ?? feedbackNumber ?? widget.min;

  function beginDrag() {
    dragBaselineRef.current = feedbackNumber ?? widget.min;
    setDragValue(dragBaselineRef.current);
  }

  function updateDrag(v: number) {
    setDragValue(clamp(v, widget.min, widget.max));
  }

  async function endDrag() {
    const dragged = dragValue;
    const baseline = dragBaselineRef.current;
    dragBaselineRef.current = null;
    setDragValue(null);
    if (dragged === null || baseline === null) return;
    const clamped = clamp(dragged, widget.min, widget.max);
    if (Math.abs(clamped - baseline) <= deadband) {
      // inside deadband: silent revert to the controller's feedback.
      return;
    }
    await commit(clamped);
  }

  async function commit(target: number) {
    setPending({ target, state: 'sending' });
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.commandPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPending(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPending({ target, state: 'rejected' });
          return;
        }
      }
      setPending(null);
    } catch {
      setPending(null);
    }
  }

  const feedbackMarkerPct = feedbackNumber !== null
    ? pct(feedbackNumber, widget.min, widget.max)
    : null;

  return (
    <div class={`hv-slider${stale ? ' hv-slider--stale' : ''}${pending ? ` hv-slider--${pending.state}` : ''}`}>
      {widget.label && <div class="hv-slider__label">{widget.label}</div>}
      <div class="hv-slider__readout">
        <span class="hv-slider__value">
          {trackValue != null ? formatValue(trackValue, step) : '—'}
        </span>
        {widget.unit && <span class="hv-slider__unit">{widget.unit}</span>}
      </div>
      <div class="hv-slider__track-wrap">
        <input
          type="range"
          class="hv-slider__input"
          min={widget.min}
          max={widget.max}
          step={step ?? 'any'}
          value={trackValue}
          onPointerDown={beginDrag}
          onInput={(e) => updateDrag(Number((e.currentTarget as HTMLInputElement).value))}
          onPointerUp={() => void endDrag()}
          onPointerCancel={() => void endDrag()}
          onTouchEnd={() => void endDrag()}
          onMouseUp={() => void endDrag()}
          aria-valuemin={widget.min}
          aria-valuemax={widget.max}
          aria-valuenow={trackValue}
        />
        {feedbackMarkerPct !== null && (
          <span
            class="hv-slider__feedback-marker"
            style={{ left: `${feedbackMarkerPct}%` }}
            title="Regler-Feedback"
            aria-hidden="true"
          />
        )}
      </div>
      <div class="hv-slider__meta">
        <span class="muted">
          {widget.min}{widget.unit ? ` ${widget.unit}` : ''}
          {' – '}
          {widget.max}{widget.unit ? ` ${widget.unit}` : ''}
        </span>
        {pending?.state === 'sending' && (
          <span class="hv-slider__pending--sending">… gesendet, warte auf Regler</span>
        )}
        {pending?.state === 'rejected' && (
          <span class="hv-slider__pending--rejected">Regler hat den Wert verworfen</span>
        )}
      </div>
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function pct(v: number, min: number, max: number): number {
  if (max <= min) return 0;
  return ((v - min) / (max - min)) * 100;
}

/** Match the step's decimal precision when possible; fall back to 1 decimal
 *  for continuous sliders so the readout doesn't jitter with float noise. */
function formatValue(v: number, step: number | undefined): string {
  if (step === undefined) return formatNumber(v, 1);
  const s = String(step);
  const dotIx = s.indexOf('.');
  const decimals = dotIx >= 0 ? s.length - dotIx - 1 : 0;
  return formatNumber(v, decimals);
}
